import styled from '@emotion/styled'
import { AdmRule, FloorRule, LazyLoadingRule, RefreshRule, RuleItem } from '@pubstack/common/src/adm/rules'
import { DateTime } from 'luxon'
import { FunctionComponent, PropsWithChildren, useState } from 'react'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import Chip from '~/components/Chip'
import { Icon } from '~/components/Icon'
import { Link } from '~/components/Link'
import { VerticalTimelineItem } from '~/components/VerticalTimelineItem'
import { useProduct } from '~/hooks/useProduct'
import { WithClassName } from '~/types/utils'

const RuleHistoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Bloc = styled.div`
  ${Fonts.P3}
  color: ${Colors.Hurricane};
  & h2 {
    ${Fonts.P2}
    font-weight: 400;
    color: ${Colors.Jet};
  }
  & div {margin-bottom: 4px;}
`
const HistoryWrapper = styled.div`
  padding-left: 12px;
  ${VerticalTimelineItem}:last-child {
    & div {
      min-height: 16px;
    }
  }
`
const MoreLink = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding-left: 4px;
`

const FloorSetting = styled.div`
  display: flex;
  flex-direction: column;
  margin: 8px 0;
  & ${Chip} {
    margin-right: 4px;
  }
    div:first-of-type {
    ${Fonts.P3}
    color: ${Colors.Jet};
    margin-bottom: 0px;
  }
`

const DefaultFloorLabel = styled.span`
  ${Fonts.P3}
  color: ${Colors.Jet};
  `

const IconBackground = styled.div`
  background-color: ${() => Colors.Kaiminus};
  width: 24px;
  height: 24px;
  border-radius: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
`

const DynamicFloorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

type RuleHistoryProps = WithClassName & {
  item: RuleItem
  ruleHistory: AdmRule[]
}

export const RuleHistory: FunctionComponent<PropsWithChildren<RuleHistoryProps>> = ({ item, ruleHistory }) => {
  const { product } = useProduct()
  const [seeMoreVersions, setSeeMoreVersions] = useState<boolean>(false)
  const data = seeMoreVersions ? ruleHistory : ruleHistory.slice(0, 5)
  const getViewportChip = (value: number) => {
    return <Chip color={Colors.Sora} text={value + ' viewport' + (value > 1 ? 's' : '')} variant={'small'} />
  }
  const displayBloc = (rule: AdmRule, attribute: string) => {
    if (attribute === 'floor' && (rule as FloorRule).floor) {
      return (
        <Bloc>
          <h2>Rule definition</h2>
          <DefaultFloorLabel>Default</DefaultFloorLabel>
          <div>{(rule as FloorRule).floor / 100} €</div>
          {(rule as FloorRule).settings && (
            <div>
              {(rule as FloorRule).settings!.map((setting, index) => {
                return (
                  <FloorSetting key={index}>
                    <div>Setting {index + 1}</div>
                    <div>{setting.floor / 100} €</div>
                    <div>
                      {setting.mediaTypes.map((mediaType) => {
                        return (
                          <IconBackground key={mediaType}>
                            <Icon fill={Colors.White} name={mediaType === 'video' ? 'video' : mediaType === 'banner' ? 'banner' : 'native'} width={'16px'} />
                          </IconBackground>
                        )
                      })}
                    </div>
                    {product !== 'pbs' && (
                      <div>
                        Sizes{' '}
                        {setting.sizes.map((size) => {
                          return <Chip key={size} text={size} color={Colors.Sora}></Chip>
                        })}
                      </div>
                    )}
                  </FloorSetting>
                )
              })}
            </div>
          )}
          {product !== 'pbs' && (
            <DynamicFloorInfo>
              <h2>Dynamic GAM UPR</h2>
              {(rule as FloorRule).dynamic ? <Icon name={'check'} fill={Colors.Success} width={'1.125rem'} /> : <Icon name={'close'} fill={Colors.Ash} width={'1.125rem'} />}
            </DynamicFloorInfo>
          )}
        </Bloc>
      )
    }
    if (attribute === 'headerBiddingEnabled' && (rule as LazyLoadingRule).headerBiddingEnabled) {
      return (
        <Bloc>
          <h2>Header bidding</h2>
          <div>Auction {getViewportChip((rule as LazyLoadingRule).prebidAuctionViewportsPercent || 0)}</div>
        </Bloc>
      )
    }
    if (attribute === 'gamEnabled' && (rule as LazyLoadingRule).gamEnabled) {
      return (
        <Bloc>
          <h2>GAM</h2>
          <div>Request {getViewportChip((rule as LazyLoadingRule).googletagFetchViewportsPercent || 0)}</div>
          <div>Render impression {getViewportChip((rule as LazyLoadingRule).googletagRenderViewportsPercent || 0)}</div>
        </Bloc>
      )
    }
    if (attribute === 'timer' && (rule as RefreshRule).timer) {
      return (
        <Bloc>
          <h2>Viewability timer</h2> {(rule as RefreshRule).timer}s
        </Bloc>
      )
    }
    if (attribute === 'capping' && (rule as RefreshRule).capping) {
      return (
        <Bloc>
          <h2>Capping</h2> {(rule as RefreshRule).capping} impressions
        </Bloc>
      )
    }
  }

  return (
    <HistoryWrapper>
      {data.map((rule) => {
        return (
          <VerticalTimelineItem
            key={rule.id}
            eventDate={DateTime.fromISO(rule.updatedAt.toString()).toFormat('dd LLL. yyyy, hh:mm a')}
            author={rule.updatedBy}
            trackingLabel={`adm-history-${item.replaceAll(' ', '-').toLocaleLowerCase()}-view`}
          >
            <RuleHistoryWrapper>
              <Bloc>
                <h2>Identification name</h2> {rule.name}
              </Bloc>
              {displayBloc(rule, 'floor')}
              {displayBloc(rule, 'headerBiddingEnabled')}
              {displayBloc(rule, 'gamEnabled')}
              {displayBloc(rule, 'timer')}
              {displayBloc(rule, 'capping')}
            </RuleHistoryWrapper>
          </VerticalTimelineItem>
        )
      })}
      {ruleHistory.length > 5 && (
        <MoreLink>
          <Icon width={'10px'} name={'circle_white'} fill={Colors.King} />
          <Link
            label={seeMoreVersions ? 'Less versions' : 'More versions'}
            trackingLabel={`adm-history-${item.replaceAll(' ', '-').toLocaleLowerCase()}-more-view`}
            onClick={() => setSeeMoreVersions(!seeMoreVersions)}
          />
        </MoreLink>
      )}
    </HistoryWrapper>
  )
}
